import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useAppContext } from '@prisjakt/ui-app-context';

import { H3Text } from '@prisjakt/ui/text/variants';
import {
  VisualNavigation,
  VisualNavigationLink,
  VisualNavigationLinkProps,
} from '@prisjakt/ui/visual-navigation';

import { useEffectOnMount } from 'src/hooks/useEffectOnMount';

import {
  trackSearchResultBrandClicked,
  trackSearchResultBrandsListed,
} from '../../tracking';

import { Brand } from 'src/types';
import { TabAnchor } from '../navigation/types';

import { Section } from './styles';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';

type Props = {
  active: boolean;
  brands: (Omit<Brand, 'media'> | null)[];
  count: number;
  query: string;
  visible: boolean;
  wideContent: boolean;
};

type FormattedLink = {
  linkProps: VisualNavigationLinkProps;
  path: string;
};

export const BrandResults = (props: Props) => {
  const { active, brands, count, query, visible, wideContent } = props;

  const { t } = useAppContext();

  useEffectOnMount(() => {
    if (brands.length) trackSearchResultBrandsListed();
  });

  const formattedLinks: FormattedLink[] = useMemo(
    () =>
      brands?.map(brand => {
        const link: VisualNavigationLinkProps = {
          label: brand?.name || '',
          imageSrc: brand?.brandLogo || undefined,
          linkContainer: Link,
          linkContainerProps: {
            to: brand?.pathName,
            'data-test': 'InternalLink',
            onClick: () =>
              trackSearchResultBrandClicked(
                brand?.id?.toString() || '',
                brand?.name || '',
                query
              ),
          },
        };
        return {
          linkProps: link,
          path: brand?.pathName || '',
        };
      }) ?? [],

    [brands, query]
  );

  if (!brands.length) return null;

  return (
    <Section id={TabAnchor.BRANDS} $visible={visible}>
      <H3Text>{`${t('search', 'brands.title')} (${count})`}</H3Text>

      <VisualNavigation compact={!active} linksPerRow={wideContent ? 5 : 4}>
        {formattedLinks.map((link, index) => (
          <VisualNavigationLink
            {...link.linkProps}
            key={link.path}
            containerProps={{
              onClick: () =>
                trackInternalNavigationClick({
                  scope: InternalNavigationScope.BRAND_CARD,
                  nextPage: link.path,
                  index,
                }),
            }}
          />
        ))}
      </VisualNavigation>
    </Section>
  );
};
